import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/src/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@repo/ui/src/components/ui/dialog';
import type { PayRateRuleDto, PayRateUnit, TeachingRole } from '@repo/shared-types';
import { useUpsertPaySetting } from '../hooks/usePayroll';
import { formatVnd } from '../lib/formatVnd';

export interface TeacherPayRateDialogInitial {
  rules: PayRateRuleDto[];
  rateUnit: PayRateUnit;
  baseSalary: number | null;
}

interface TeacherPayRateDialogProps {
  membershipId: string;
  teacherName: string;
  initial: TeacherPayRateDialogInitial | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The ONE place a teacher's pay rate is edited — opened from both
 * TeamTab's per-member kebab menu and the payroll table's rate column.
 * Do not fork this into a second copy: an earlier bug in this repo came
 * from exactly that kind of drift between two rate forms.
 */
export function TeacherPayRateDialog({
  membershipId,
  teacherName,
  initial,
  open,
  onOpenChange,
}: TeacherPayRateDialogProps) {
  const { t } = useTranslation('payroll');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('settings.title', { name: teacherName })}</DialogTitle>
          <DialogDescription>{t('settings.description')}</DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, keyed on the teacher — so its form state
            (a plain useState seeded from `initial`) always starts fresh
            instead of leaking a previous teacher's in-progress edits or a
            previous open's unsaved typing. No effect-based resync needed. */}
        {open && (
          <PayRateForm
            key={membershipId}
            membershipId={membershipId}
            initial={initial}
            onSaved={() => onOpenChange(false)}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface PayRateFormProps {
  membershipId: string;
  initial: TeacherPayRateDialogInitial | null;
  onSaved: () => void;
  onCancel: () => void;
}

// A row while it is being typed: the numbers are strings so a half-typed
// "1" in the threshold field doesn't get coerced into a saved rule.
interface DraftRule {
  role: TeachingRole;
  minStudents: string;
  rate: string;
}

// A default session length for the live worked example only — it does not
// affect what gets saved. Real per-session pay ignores duration entirely;
// this just gives an hourly rate a concrete number to anchor on.
const EXAMPLE_SESSION_MINUTES = 90;

function PayRateForm({ membershipId, initial, onSaved, onCancel }: PayRateFormProps) {
  const { t } = useTranslation('payroll');
  const { t: tc } = useTranslation('common');
  const [rules, setRules] = useState<DraftRule[]>(() =>
    (initial?.rules ?? []).map((r) => ({
      role: r.role,
      minStudents: String(r.minStudents),
      rate: String(r.rate),
    })),
  );
  const [rateUnit, setRateUnit] = useState<PayRateUnit>(() => initial?.rateUnit ?? 'PER_SESSION');
  const [baseSalaryStr, setBaseSalaryStr] = useState(() =>
    initial?.baseSalary != null ? String(initial.baseSalary) : '',
  );
  const upsertPaySetting = useUpsertPaySetting(membershipId);

  // Parse once; the submit guard, the duplicate message and the live example
  // all read off this so the form can never save something it didn't show.
  const parsed = rules.map((r) => ({
    role: r.role,
    minStudents: Math.round(Number(r.minStudents)),
    rate: Math.round(Number(r.rate)),
    valid:
      r.minStudents.trim() !== '' &&
      r.rate.trim() !== '' &&
      Number.isFinite(Number(r.minStudents)) &&
      Number.isFinite(Number(r.rate)) &&
      Math.round(Number(r.minStudents)) >= 1 &&
      Math.round(Number(r.rate)) >= 0,
  }));
  const duplicateIdx = new Set<number>();
  const seen = new Map<string, number>();
  parsed.forEach((r, i) => {
    if (!r.valid) return;
    const key = `${r.role}:${r.minStudents}`;
    if (seen.has(key)) duplicateIdx.add(i);
    else seen.set(key, i);
  });
  const canSubmit = parsed.every((r) => r.valid) && duplicateIdx.size === 0;

  // The worked example anchors an abstract number to a real buổi — kept from
  // the single-rate form, now driven by the LOWEST CHINH band (the one a
  // teacher pictures when they think "what do I earn per buổi").
  const exampleRate = parsed
    .filter((r) => r.valid && r.role === 'CHINH')
    .sort((a, b) => a.minStudents - b.minStudents)[0]?.rate;

  function updateRule(index: number, patch: Partial<DraftRule>) {
    setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function removeRule(index: number) {
    setRules((prev) => prev.filter((_, i) => i !== index));
  }

  function addRule() {
    setRules((prev) => [...prev, { role: 'CHINH', minStudents: '1', rate: '' }]);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    upsertPaySetting.mutate(
      {
        rateUnit,
        baseSalary: baseSalaryStr.trim() ? Number(baseSalaryStr) : null,
        rules: parsed.map((r) => ({ role: r.role, minStudents: r.minStudents, rate: r.rate })),
      },
      { onSuccess: onSaved },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-sm font-medium text-surface-700">{t('settings.rules')}</Label>
        <p className="text-xs text-surface-500">{t('settings.rulesHelper')}</p>
        {rules.length > 0 && (
          <div className="mt-2 space-y-2">
            <div className="flex items-end gap-2">
              <span className="w-32 text-xs font-medium text-surface-500">{t('settings.ruleRole')}</span>
              <span className="w-28 text-xs font-medium text-surface-500">{t('settings.ruleMinStudents')}</span>
              <span className="flex-1 text-xs font-medium text-surface-500">{t('settings.ruleRate')}</span>
              <span className="w-[44px]" />
            </div>
            {rules.map((rule, index) => (
              <div key={index}>
                <div className="flex items-end gap-2">
                  <Select
                    value={rule.role}
                    onValueChange={(v) => updateRule(index, { role: v as TeachingRole })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CHINH">{t('settings.roleChinh')}</SelectItem>
                      <SelectItem value="TRO_GIANG">{t('settings.roleTroGiang')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="1"
                    className="w-28"
                    aria-label={t('settings.ruleMinStudents')}
                    value={rule.minStudents}
                    onChange={(e) => updateRule(index, { minStudents: e.target.value })}
                  />
                  <Input
                    type="number"
                    min="0"
                    className="flex-1"
                    aria-label={t('settings.ruleRate')}
                    value={rule.rate}
                    onChange={(e) => updateRule(index, { rate: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => removeRule(index)}
                    aria-label={t('settings.removeRule')}
                    className="flex min-h-[44px] w-[44px] shrink-0 items-center justify-center text-surface-500 hover:text-destructive sm:min-h-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {duplicateIdx.has(index) && (
                  <p className="text-xs text-destructive">
                    {t('settings.ruleDuplicate', { minStudents: rule.minStudents })}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        {rules.length === 0 && <p className="mt-2 text-sm text-surface-500">{t('settings.noRules')}</p>}
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addRule}>
          <Plus className="h-4 w-4" />
          {t('settings.addRule')}
        </Button>
      </div>
      <div>
        <Label className="text-sm font-medium text-surface-700">{t('settings.unit')}</Label>
        <Select value={rateUnit} onValueChange={(v) => setRateUnit(v as PayRateUnit)}>
          <SelectTrigger className="mt-1.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PER_SESSION">{t('settings.perSession')}</SelectItem>
            <SelectItem value="PER_HOUR">{t('settings.perHour')}</SelectItem>
          </SelectContent>
        </Select>
        {exampleRate !== undefined && (
          <p className="mt-1.5 text-xs text-surface-500">
            {rateUnit === 'PER_HOUR'
              ? t('settings.exampleHour', {
                  minutes: EXAMPLE_SESSION_MINUTES,
                  amount: formatVnd(Math.round((exampleRate * EXAMPLE_SESSION_MINUTES) / 60)),
                })
              : t('settings.exampleSession', { amount: formatVnd(exampleRate) })}
          </p>
        )}
      </div>
      <div>
        <Label htmlFor="pay-base-salary" className="text-sm font-medium text-surface-700">
          {t('settings.baseSalary')}
        </Label>
        <Input
          id="pay-base-salary"
          type="number"
          min="0"
          value={baseSalaryStr}
          onChange={(e) => setBaseSalaryStr(e.target.value)}
          placeholder="0"
          className="mt-1.5"
        />
        <p className="mt-1.5 text-xs text-surface-500">{t('settings.baseSalaryHelper')}</p>
      </div>

      {upsertPaySetting.error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2">
          <p className="text-sm text-red-700">{(upsertPaySetting.error as Error).message}</p>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {tc('cancel')}
        </Button>
        <Button type="submit" disabled={!canSubmit || upsertPaySetting.isPending} className="font-semibold">
          {t('settings.save')}
        </Button>
      </DialogFooter>
    </form>
  );
}
